import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ArrowDown, HandHeart, Footprints, HeartHandshake, Network, Megaphone, Share2, MessageCircle } from "lucide-react";
import SEO from "../components/SEO";
import Marquee from "../components/Marquee";
import { Reveal, LineReveal } from "../components/Reveal";
import { EventCard, StoryCard, ActivityCard } from "../components/cards";
import { getUpcomingEvents, getLatestStories, getActivities, getPillar, getPillarOrder } from "../lib/content";
import { marqueeItems, images, involveOptions } from "../content/site";
import CommunityTiles from "../components/CommunityTiles";

const optionIcons = { HandHeart, Footprints, HeartHandshake, Network, Megaphone, Share2 };

function Community() {
  return (
    <section className="py-24 md:py-32 border-t border-line" data-testid="home-community">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Community</p>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink max-w-3xl">
            Join the PilCoo community
          </h2>
          <p className="mt-5 max-w-xl text-base md:text-lg text-ink-2 leading-relaxed">
            There's more than one way to show up.
          </p>
        </Reveal>
        <CommunityTiles
          tiles={[
            {
              key: "pacer",
              eyebrow: "Pacer Community",
              title: "Move with us",
              text: "Join The PilCoo Foundation on Pacer to take part in walking challenges, community goals and future movement-led initiatives.",
              cta: "Join our Pacer Community",
            },
            {
              key: "whatsapp",
              eyebrow: "WhatsApp Channel",
              title: "Stay connected",
              text: "Follow The PilCoo Foundation on WhatsApp for activities, volunteering opportunities, events, stories and ways to participate.",
              cta: "Follow our WhatsApp Channel",
            },
          ]}
        />
      </div>
    </section>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const ySmall = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 md:pt-40 pb-16 md:pb-24" data-testid="home-hero">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex items-center gap-3 text-xs md:text-sm uppercase tracking-[0.22em] font-medium text-ink-2"
          >
            <span className="flex gap-1.5">
              <span className="h-2 w-2 rounded-full bg-pillar-people" />
              <span className="h-2 w-2 rounded-full bg-pillar-nature" />
              <span className="h-2 w-2 rounded-full bg-pillar-animals" />
            </span>
            The PilCoo Foundation
          </motion.p>

          <h1 className="mt-6 font-serif tracking-tighter leading-[0.95] text-ink text-[15vw] sm:text-7xl lg:text-[6.2rem]">
            <LineReveal lines={["Kindness needs", "somewhere to go."]} delay={0.25} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink-2"
          >
            The PilCoo Foundation connects people who want to help with causes,
            communities and organisations where that help can create meaningful
            change — across <span className="text-ink font-medium">animals</span>,{" "}
            <span className="text-ink font-medium">people</span> and{" "}
            <span className="text-ink font-medium">nature</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/activities"
              data-testid="hero-cta-primary"
              className="group rounded-full bg-action text-sand px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 transition-transform duration-300 hover:scale-[1.04] active:scale-95 hover:bg-action-hover"
            >
              See what we're doing
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/get-involved"
              data-testid="hero-cta-secondary"
              className="rounded-full border border-ink px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-ink hover:text-sand"
            >
              Get involved
            </Link>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4 }}
            style={{ y: yMain }}
            className="relative overflow-hidden rounded-xl aspect-[4/5] max-w-md ml-auto"
          >
            <img
              src={images.heroMain}
              alt="Many hands coming together in a circle of support"
              className="h-full w-full object-cover"
              fetchPriority="high"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            style={{ y: ySmall }}
            className="absolute -bottom-8 -left-2 sm:left-4 w-36 sm:w-44 aspect-square overflow-hidden rounded-xl border-4 border-sand"
          >
            <img
              src={images.heroSecondary}
              alt="A person's hand gently holding a dog's paw"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="mt-16 flex justify-center"
        aria-hidden="true"
      >
        <span className="flex flex-col items-center gap-2 text-[0.65rem] uppercase tracking-[0.3em] text-ink-2">
          Scroll
          <ArrowDown className="h-4 w-4 animate-bounce" />
        </span>
      </motion.div>
    </section>
  );
}

function Pillars() {
  return (
    <section className="py-24 md:py-32" data-testid="home-pillars">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">The three pillars</p>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink max-w-2xl">
            Three pillars. <em className="text-ink-2">One promise.</em>
          </h2>
          <p className="mt-6 max-w-xl text-base md:text-lg text-ink-2 leading-relaxed">
            PilCoo stands equally on three kinds of care. None is a side project;
            each is a door into helping.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {getPillarOrder().map((key, i) => {
            const pillar = getPillar(key);
            return (
              <Reveal key={key} delay={i * 0.12}>
                <Link
                  to={`/${key}`}
                  data-testid={`pillar-card-${key}`}
                  className="group flex flex-col h-full border border-line rounded-xl overflow-hidden bg-sand transition-colors duration-500 hover:bg-sand-2"
                >
                  <div className="overflow-hidden aspect-[4/3]">
                    <img
                      src={pillar.image}
                      alt={pillar.imageAlt}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-col grow p-7 md:p-8">
                    <div className="flex items-center justify-between">
                      <span className="font-serif text-sm italic" style={{ color: pillar.accentText }}>
                        0{i + 1}
                      </span>
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: pillar.accent }} />
                    </div>
                    <h3 className="mt-3 font-serif text-3xl tracking-tight text-ink">{pillar.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink-2 grow">{pillar.shortDescription}</p>
                    <span
                      className="mt-6 inline-flex items-center gap-2 text-sm font-semibold"
                      style={{ color: pillar.accentText }}
                    >
                      {pillar.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Happening() {
  const upcoming = getUpcomingEvents(3);
  if (upcoming.length === 0) return null;
  return (
    <section className="py-24 md:py-32 border-t border-line" data-testid="home-events">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Events</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
              Happening at PilCoo
            </h2>
          </div>
          <Link
            to="/events"
            data-testid="home-events-see-all"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-action"
          >
            See all events
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className={`mt-12 grid grid-cols-1 gap-6 ${upcoming.length >= 3 ? "md:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2 max-w-4xl"}`}>
          {upcoming.map((event, i) => (
            <Reveal key={event.slug} delay={i * 0.1}>
              <EventCard event={event} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function KindnessInMotion() {
  return (
    <section className="py-24 md:py-32 bg-sand-2 border-y border-line" data-testid="home-activities">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Current activities</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
              Kindness in Motion
            </h2>
            <p className="mt-5 max-w-xl text-base md:text-lg text-ink-2 leading-relaxed">
              Ongoing initiatives and community challenges PilCoo is working
              on — ways to take part at your own pace, in your own way.
            </p>
          </div>
          <Link
            to="/activities"
            data-testid="home-activities-see-all"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-action"
          >
            All activities
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6">
          {getActivities().map((activity, i) => (
            <Reveal key={activity.slug} delay={i * 0.1}>
              <ActivityCard activity={activity} featured />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function StoriesHome() {
  const [featured, ...rest] = getLatestStories(3);
  return (
    <section className="py-24 md:py-32" data-testid="home-stories">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Stories</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
              Stories from PilCoo
            </h2>
          </div>
          <Link
            to="/stories"
            data-testid="home-stories-see-all"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-action"
          >
            Explore all stories
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <Reveal className="lg:col-span-7">
            <StoryCard story={featured} featured />
          </Reveal>
          <div className="lg:col-span-5 flex flex-col gap-10">
            {rest.map((story, i) => (
              <Reveal key={story.slug} delay={0.1 + i * 0.1}>
                <StoryCard story={story} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ImpactTeaser() {
  return (
    <section className="relative py-28 md:py-36 bg-ink text-sand overflow-hidden" data-testid="home-impact">
      <img
        src={images.impact}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-15"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-medium text-sand/60">Our impact</p>
          <h2 className="mt-5 font-serif text-4xl md:text-6xl tracking-tight max-w-3xl leading-[1.02]">
            Our impact begins here — <em className="text-sand/70">and it will be real.</em>
          </h2>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-sand/70">
            PilCoo is at the beginning of its story. We won't publish numbers we
            can't stand behind — so this space is intentionally quiet. When the
            first walk is walked, the first meal sponsored, the first tree
            planted, the counts will live here. Every one of them documented.
          </p>
          <Link
            to="/impact"
            data-testid="home-impact-link"
            className="group mt-10 inline-flex items-center gap-2 rounded-full border border-sand/40 px-7 py-3.5 text-sm font-semibold text-sand transition-colors duration-300 hover:bg-sand hover:text-ink"
          >
            See how we'll measure it
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function GetInvolvedTeaser() {
  return (
    <section className="py-24 md:py-32" data-testid="home-get-involved">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Get involved</p>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink max-w-3xl">
            There isn't only <em className="text-ink-2">one way</em> to help.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {involveOptions.map((opt, i) => {
            const Icon = optionIcons[opt.icon];
            return (
              <Reveal key={opt.key} delay={i * 0.08}>
                <Link
                  to="/get-involved"
                  data-testid={`involve-card-${opt.key}`}
                  className="group flex flex-col h-full border border-line rounded-xl p-7 md:p-8 bg-sand transition-colors duration-500 hover:bg-sand-2"
                >
                  <span className="h-11 w-11 rounded-full bg-sand-3 flex items-center justify-center text-action transition-transform duration-300 group-hover:scale-110">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-serif text-2xl tracking-tight text-ink">{opt.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-2 grow">{opt.text}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-action">
                    Start here
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <SEO path="/" />
      <Hero />
      <Marquee items={marqueeItems} />
      <Pillars />
      <Happening />
      <KindnessInMotion />
      <Community />
      <StoriesHome />
      <ImpactTeaser />
      <GetInvolvedTeaser />
    </main>
  );
}
